import { createContext, useContext, useMemo, type ReactNode } from "react";

export const STAFF_BANNER_HEIGHT = 34;
export const TIMEZONE_BANNER_HEIGHT = 32;

export type BottomBannersContextValue = {
  timezoneBannerShown: boolean;
  staffBannerShown: boolean;
  isLoggedInAs: boolean;
};

const BottomBannersContext = createContext<BottomBannersContextValue>({
  timezoneBannerShown: false,
  staffBannerShown: false,
  isLoggedInAs: false,
});

type BottomBannersProviderProps = {
  timezoneBannerShown: boolean;
  isLoggedInAs: boolean;
  loggedInAsUserName: string | null;
  children: ReactNode;
};

export function BottomBannersProvider({
  timezoneBannerShown,
  isLoggedInAs,
  loggedInAsUserName,
  children,
}: BottomBannersProviderProps) {
  const value = useMemo(
    () => ({
      timezoneBannerShown,
      staffBannerShown: isLoggedInAs && Boolean(loggedInAsUserName),
      isLoggedInAs,
    }),
    [timezoneBannerShown, isLoggedInAs, loggedInAsUserName],
  );

  return <BottomBannersContext.Provider value={value}>{children}</BottomBannersContext.Provider>;
}

export function useBottomBanners() {
  return useContext(BottomBannersContext);
}

export function getBottomBannerOffset({
  timezoneBannerShown,
  staffBannerShown,
  isLoggedInAs,
}: BottomBannersContextValue): number {
  // TimezoneBanner stacks above the staff banner when the logged-in-as cookie exists,
  // even before LoggedInAsBanner renders with a username.
  const staffStackHeight = staffBannerShown || isLoggedInAs ? STAFF_BANNER_HEIGHT : 0;

  if (timezoneBannerShown) {
    return staffStackHeight + TIMEZONE_BANNER_HEIGHT;
  }

  return staffBannerShown ? STAFF_BANNER_HEIGHT : 0;
}
